import { create } from 'zustand';
import { QuerySolarBuildingInsightsResponse } from '../lib/api';
import { WidgetState } from '../types/states';

// Energy calculation constants
export const AVERAGE_PRICE_PER_KWH = 0.27; // From: https://energyguide.org.uk/average-cost-electricity-kwh-uk/
export const FEED_IN_TARIFF_PRICE_PER_KWH = 0.09; // From: https://www.ofgem.gov.uk/environmental-programmes/fit/fit-tariff-rates
export const COST_INCREASE_FACTOR = 1.025; // 2.5% increase per year
export const DC_TO_AC_DERATE = 0.968; // >96.8% efficiency from DC to AC (RHI-(3-5)K-48ES Datasheet)
export const ANNUAL_DISCOUNT_RATE = 1.015; // 1.5% annual discount estimated
export const EFFICIENCY_DEPRECIATION_FACTOR = 0.995; // 0.5% annual decrease in solar panel efficiency (https://www.greenmatch.co.uk/blog/2015/01/the-lifespan-of-solar-panels)
export const INCENTIVES = 0;
export const FORCED_EXPORT_KWH_COEFFICIENT = 0.9; // 50% of produced energy is always exported if there is no battery
export const SELF_CONSUMPTION_SLOPE = 0.5; // 0.5 is a constant that represents the ratio of energy that is exported to the grid
export const BATTERY_ENABLED_CONSTANT = 4; // 4 times more energy is exported to the grid if there is no battery

export enum SolarPanelType {
    PREMIUM,
    PREMIUM_PLUS,
}

export const SolarPanelTypeData = {
    [SolarPanelType.PREMIUM]: {
        name: 'Premium',
        description: 'Suited for those who want premium quality solar panels at an affordable price.',
        price: 560,
        wattage: 410,
        fallbackKwh: 360,
        coefficient: 1, // # 1 x Premium panel
        widthMeters: 1.05, // DUMMY DATA
        heightMeters: 1.75, // DUMMY DATA
        lifespanYears: 30,
        imgSrc: 'https://storage.googleapis.com/ebc-solar-quotation-widget/assets/dmeg-solar-panels.png',
    },
    [SolarPanelType.PREMIUM_PLUS]: {
        name: 'Premium Plus',
        description: 'Highest performance panels we offer for those who want the best of the best in solar panels.',
        price: 580,
        wattage: 415,
        fallbackKwh: 370,
        coefficient: 415 / 410, // # (premiumplus_wattage / premium_wattage) x Premium panel
        widthMeters: 1.05, // DUMMY DATA
        heightMeters: 1.75, // DUMMY DATA
        lifespanYears: 30,
        imgSrc: 'https://storage.googleapis.com/ebc-solar-quotation-widget/assets/dmeg-solar-panels.png',
    },
};

export enum BatteryType {
    STANDARD,
}

export const BatteryTypeData = {
    [BatteryType.STANDARD]: {
        name: 'Standard',
        price: 2400,
        imgSrc: 'https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-2.png',
    },
};

export type QuotationData = {
    totalPrice: number;

    solarPanelType: SolarPanelType;
    numberOfSolarPanels: number;

    batteryEnabled: boolean;
    batteryType: BatteryType;
    annualPctBillIncrease: number;

    estimatedAnnualKwhProduction: number;
    estimatedKilowattPeak: number; // kWp
    estimatedMonthlySavingsRangeStart: number;
    estimatedMonthlySavingsRangeEnd: number;
    estimatedYearlyCO2Savings: number;
    totalEstimatedSavingsRangeStart: number;
    totalEstimatedSavingsRangeEnd: number;

    selfConsumptionPctRangeStart: number;
    selfConsumptionPctRangeEnd: number;
};

// Configuration constants
export const DEFAULT_INSTALLATION_SIZE = 10;
export const DEFAULT_MONTHLY_BILL = 100;
export const DEFAULT_SOLAR_PANEL_TYPE = SolarPanelType.PREMIUM;
export const DEFAULT_BATTERY_ENABLED = false;
export const DEFAULT_BATTERY_TYPE = BatteryType.STANDARD;

interface EbcSolarState {
    state: WidgetState;
    setState: (state: WidgetState) => void;

    postcode: string;
    setPostcode: (postcode: string) => void;

    initialLocation: {
        lat: number;
        lng: number;
    };
    setInitialLocation: (lat: number, lng: number) => void;

    address: string;
    setAddress: (address: string) => void;

    solarData?: QuerySolarBuildingInsightsResponse;
    setSolarData: (solarData: QuerySolarBuildingInsightsResponse) => void;

    monthlyBill: number;
    setMonthlyBill: (monthlyBill: number) => void;

    quotationData: QuotationData;
    setQuotationData: (quotationData: QuotationData) => void;
    calculateQuotationData: () => void;

    ctaSqueezePopupModalOpen: boolean;
    setCtaSqueezePopupModalOpen: (ctaSqueezePopupModalOpen: boolean) => void;
}

const useEbcSolarStore = create<EbcSolarState>()((set) => ({
    state: WidgetState.ENTER_POSTCODE,
    setState: (state) => set({ state }),

    postcode: '',
    setPostcode: (postcode) => set({ postcode }),

    initialLocation: {
        lat: 0,
        lng: 0,
    },

    setInitialLocation: (lat, lng) => set({ initialLocation: { lat, lng } }),

    address: '',
    setAddress: (address) => set({ address }),

    solarData: undefined,
    setSolarData: (solarData) => set({ solarData }),

    monthlyBill: DEFAULT_MONTHLY_BILL,
    setMonthlyBill: (monthlyBill: number) => set({ monthlyBill }),

    quotationData: {
        totalPrice: 0,
        solarPanelType: DEFAULT_SOLAR_PANEL_TYPE,
        numberOfSolarPanels: DEFAULT_INSTALLATION_SIZE,
        batteryEnabled: DEFAULT_BATTERY_ENABLED,
        batteryType: DEFAULT_BATTERY_TYPE,
        annualPctBillIncrease: COST_INCREASE_FACTOR,
        estimatedAnnualKwhProduction: 0,
        estimatedKilowattPeak: 0,
        estimatedMonthlySavingsRangeStart: 0,
        estimatedMonthlySavingsRangeEnd: 0,
        estimatedYearlyCO2Savings: 0,
        totalEstimatedSavingsRangeStart: 0,
        totalEstimatedSavingsRangeEnd: 0,
        selfConsumptionPctRangeStart: 0,
        selfConsumptionPctRangeEnd: 0,
    },
    setQuotationData: (quotationData) => set({ quotationData }),

    calculateQuotationData: () =>
        set((state) => {
            const solarData = state.solarData;
            const monthlyBill = state.monthlyBill;
            const numberOfSolarPanels = state.quotationData.numberOfSolarPanels;
            const solarPanelType = state.quotationData.solarPanelType;
            const batteryEnabled = state.quotationData.batteryEnabled;
            const batteryType = state.quotationData.batteryType;

            // Get solar panel data
            const solarPanelData = SolarPanelTypeData[solarPanelType];
            const pricePerPanel = solarPanelData.price;
            const panelKwhCoefficient = solarPanelData.coefficient;
            const wattagePerPanel = solarPanelData.wattage;
            const fallbackYearlyKwh = solarPanelData.fallbackKwh * numberOfSolarPanels;

            // Get battery data
            const batteryData = BatteryTypeData[batteryType];
            const batteryPrice = batteryData.price;

            // Get data from solar data
            const panelConfigData = solarData?.solarPotential.solarPanelConfigs.find((config) => config.panelsCount === numberOfSolarPanels);
            const panelConfigWattage = solarData?.solarPotential.panelCapacityWatts || wattagePerPanel;
            const yearlyEnergyDcKwhCoefficient = wattagePerPanel / panelConfigWattage;

            // Set all required variables for calculations
            // Calculations from: https://developers.google.com/maps/documentation/solar/calculate-costs-non-us
            const installationSize = numberOfSolarPanels;
            const averagePricePerKwh = AVERAGE_PRICE_PER_KWH;
            const feedInTariffPricePerKwh = FEED_IN_TARIFF_PRICE_PER_KWH;
            const costIncreaseFactor = COST_INCREASE_FACTOR;
            const dcToAcDerate = DC_TO_AC_DERATE;
            const discountRate = ANNUAL_DISCOUNT_RATE;
            const efficiencyDepreciationFactor = EFFICIENCY_DEPRECIATION_FACTOR;
            const incentives = INCENTIVES;
            const installationCostModel = (installationSize: number) => {
                const totalBatteryCost = batteryEnabled ? batteryPrice : 0;
                const totalPanelsCost = installationSize * pricePerPanel;
                return totalPanelsCost + totalBatteryCost;
            };
            const installationLifeSpan = solarPanelData.lifespanYears;
            const kWhConsumptionModel = (monthlyBill: number) => monthlyBill / averagePricePerKwh;
            // monthlyBill already set
            const yearlyEnergyDcKwh = yearlyEnergyDcKwhCoefficient * (panelConfigData?.yearlyEnergyDcKwh || fallbackYearlyKwh);
            // console.log('yearlyEnergyDcKwh', yearlyEnergyDcKwh);

            // Calculation steps
            // 1. Calculate the annual energy consumption of the household at the input location
            const monthlyKWhEnergyConsumption = kWhConsumptionModel(monthlyBill);
            const annualKWhEnergyConsumption = monthlyKWhEnergyConsumption * 12;
            // console.log('annualKWhEnergyConsumption', annualKWhEnergyConsumption);

            // 2. Calculate the annual solar energy AC production
            const initialAcKwhPerYear = yearlyEnergyDcKwh * dcToAcDerate;
            // console.log('initialAcKwhPerYear', initialAcKwhPerYear);

            // 3. Calculate the lifetime cost of energy consumption if the panels are installed
            const annualProduction = (initialAcKwhPerYear: number, efficiencyDepreciationFactor: number, year: number) => {
                return initialAcKwhPerYear * Math.pow(efficiencyDepreciationFactor, year);
            };
            const annualSavingsEstimate = (
                yearlyKWhEnergyConsumption: number,
                initialAcKwhPerYear: number,
                efficiencyDepreciationFactor: number,
                year: number,
                costIncreaseFactor: number,
                discountRate: number,
                batteryEnabled: boolean
            ) => {
                const producedKwh = annualProduction(initialAcKwhPerYear, efficiencyDepreciationFactor, year);

                const consumptionProductionRatio = yearlyKWhEnergyConsumption / producedKwh;

                // How much energy is saved and exported based on household production/consumption ratio?
                const exportedRatio = Math.max(0, FORCED_EXPORT_KWH_COEFFICIENT - SELF_CONSUMPTION_SLOPE * consumptionProductionRatio);
                const savedRatio = 1 - exportedRatio;

                // console.log('exportedRatio', exportedRatio, 'productionConsumptionRatio', consumptionProductionRatio);

                let savedKwh = 0;
                let exportedKwh = 0;

                // How much energy is exported no matter what due to lack of battery capacity?
                const forcedExportKwhCoefficient = batteryEnabled ? exportedRatio / BATTERY_ENABLED_CONSTANT : exportedRatio; // Some of the produced energy is always exported if there is no battery
                exportedKwh += forcedExportKwhCoefficient * producedKwh;

                // Calculate remainder of saved and exported kWh based on household consumption
                const remainingKwh = producedKwh - exportedKwh; // same as = savedRatio * producedKwh
                const netKwh = remainingKwh - yearlyKWhEnergyConsumption;

                if (netKwh < 0) {
                    // If netKwh is negative, then the household is consuming more energy than it is producing
                    savedKwh += remainingKwh;
                } else {
                    // If netKwh is positive, then the household is producing more energy than it is consuming
                    savedKwh += yearlyKWhEnergyConsumption;

                    if (batteryEnabled === true) {
                        // If battery is enabled, then the household is storing the excess energy in the battery
                        const batteryDischargeKwh = netKwh;
                        savedKwh += batteryDischargeKwh;
                    } else {
                        // If battery is not enabled, then the excess energy is exported to the grid
                        exportedKwh += netKwh;
                    }
                }

                const savings = savedKwh * averagePricePerKwh;
                const earnings = exportedKwh * feedInTariffPricePerKwh;

                const costIncrease = Math.pow(costIncreaseFactor, year);
                const discount = Math.pow(discountRate, year);

                const totalSavings = ((savings + earnings) * costIncrease) / discount;
                return totalSavings;
            };
            const lifetimeSavings = (
                yearlyKWhEnergyConsumption: number,
                initialAcKwhPerYear: number,
                efficiencyDepreciationFactor: number,
                installationLifeSpan: number,
                costIncreaseFactor: number,
                discountRate: number,
                batteryEnabled: boolean
            ) => {
                const bill = [];
                for (let year = 0; year < installationLifeSpan; year++) {
                    bill.push(
                        annualSavingsEstimate(
                            yearlyKWhEnergyConsumption,
                            initialAcKwhPerYear,
                            efficiencyDepreciationFactor,
                            year,
                            costIncreaseFactor,
                            discountRate,
                            batteryEnabled
                        )
                    );
                }
                return bill;
            };
            const lifetimeSavingsWithSolarList = lifetimeSavings(
                annualKWhEnergyConsumption,
                initialAcKwhPerYear,
                efficiencyDepreciationFactor,
                installationLifeSpan,
                costIncreaseFactor,
                discountRate,
                batteryEnabled
            );
            const lifetimeSavingsWithSolar = lifetimeSavingsWithSolarList.reduce((a, b) => a + b, 0);
            // console.log(lifetimeSavingsWithSolarList);
            // console.log('lifetimeSavingsWithSolar', lifetimeSavingsWithSolar);

            // 4. Calculate the lifetime cost of energy consumption if the panels are not installed
            let costOfElectricityWithoutSolarList = [];
            for (let i = 0; i < installationLifeSpan; i++) {
                const bill = monthlyBill * 12 * Math.pow(costIncreaseFactor, i);
                const discount = Math.pow(discountRate, i);
                const cost = bill / discount;
                costOfElectricityWithoutSolarList.push(cost);
            }
            const costOfElectricityWithoutSolar = costOfElectricityWithoutSolarList.reduce((a, b) => a + b, 0);
            // console.log(costOfElectricityWithoutSolarList);
            // console.log('costOfElectricityWithoutSolar', costOfElectricityWithoutSolar);

            // 5. Calculate cost of installation
            const installationCost = installationCostModel(installationSize);
            // console.log('installationCost', installationCost);

            // 6. Calculate total cost electricity with solar
            // const totalCostWithSolar = installationCost + remainingLifetimeUtilityBill - incentives;
            // const totalCostWithSolar = remainingLifetimeUtilityBill - incentives;
            // console.log('totalCostWithSolar', totalCostWithSolar);

            // 7. Calculate savings
            // const savings = costOfElectricityWithoutSolar - totalCostWithSolar;
            const savings = lifetimeSavingsWithSolar;
            // console.log('savings', savings);

            const totalEstimatedSavingsRangeStart = 1000 * Math.round((savings * 0.8) / 1000); // Round to nearest 1000
            const totalEstimatedSavingsRangeEnd = 1000 * Math.round((savings * 1.0) / 1000); // Round to nearest 1000

            // 8. Calculate annual kWh production, kWp value, and self-consumption percentage
            const estimatedKilowattPeak = (wattagePerPanel / 1000) * numberOfSolarPanels;

            const totalProduction = initialAcKwhPerYear;
            const consumptionProductionRatio = annualKWhEnergyConsumption / totalProduction;
            const exportKwhRatio = Math.max(0, FORCED_EXPORT_KWH_COEFFICIENT - SELF_CONSUMPTION_SLOPE * consumptionProductionRatio);
            const realExportKwhRatio = batteryEnabled ? exportKwhRatio / BATTERY_ENABLED_CONSTANT : exportKwhRatio;
            const exportKwh = totalProduction * realExportKwhRatio;

            const selfConsumption = 5 * Math.round((100 * ((totalProduction - exportKwh) / totalProduction)) / 5);
            const selfConsumptionPctRangeStart = selfConsumption - 5;
            const selfConsumptionPctRangeEnd = selfConsumption;

            const firstYearSavings = lifetimeSavingsWithSolarList[0];
            const estimatedMonthlySavingsRangeStart = (firstYearSavings / 12) * 0.8;
            const estimatedMonthlySavingsRangeEnd = (firstYearSavings / 12) * 1.0;

            // 9. Calculate CO2 savings
            const carbonOffsetFactorKgPerMwh = solarData?.solarPotential.carbonOffsetFactorKgPerMwh || 351; // https://www.carbonfootprint.com/docs/2023_02_emissions_factors_sources_for_2022_electricity_v10.pdf
            const estimatedYearlyCO2Savings = carbonOffsetFactorKgPerMwh * (initialAcKwhPerYear / 1000);

            const newQuotationData: QuotationData = {
                totalPrice: installationCost,
                solarPanelType,
                numberOfSolarPanels,
                batteryEnabled,
                batteryType,
                estimatedAnnualKwhProduction: initialAcKwhPerYear,
                estimatedKilowattPeak,
                annualPctBillIncrease: COST_INCREASE_FACTOR,
                estimatedMonthlySavingsRangeStart,
                estimatedMonthlySavingsRangeEnd,
                estimatedYearlyCO2Savings,
                totalEstimatedSavingsRangeStart: totalEstimatedSavingsRangeStart,
                totalEstimatedSavingsRangeEnd: totalEstimatedSavingsRangeEnd,
                selfConsumptionPctRangeStart: selfConsumptionPctRangeStart,
                selfConsumptionPctRangeEnd: selfConsumptionPctRangeEnd,
            };

            // console.log('\n\n');
            return {
                ...state,
                quotationData: newQuotationData,
            };
        }),

    ctaSqueezePopupModalOpen: false,
    setCtaSqueezePopupModalOpen: (ctaSqueezePopupModalOpen) => set({ ctaSqueezePopupModalOpen }),
}));

export default useEbcSolarStore;
